/**
 * Shared helpers for OPSI CPT page sections (wp-json/wp/v2/opsi).
 * Styling is intentionally minimal until design tokens are provided.
 */

/** Section rows: no margin — gap on .opsi-page-builder handles 37px spacing. */
export const OPSI_SECTION_SPACING = "!mt-0 !mb-0";

/** Parts inside a section: no vertical margin (section wrapper handles gaps). */
export const OPSI_PART_SPACING = "mt-0 mb-0";

/** Open CMS / external resource links in a new tab. */
export const OPSI_EXTERNAL_LINK_PROPS = {
  target: "_blank",
  rel: "noopener noreferrer",
};

const API_BASE =
  process.env.NEXT_PUBLIC_BE || "https://api.liverpoolcityregion-ca.gov.uk";

/** Strip tags and whitespace to detect empty rich-text fields from WordPress. */
export const hasHtmlContent = (value) =>
  String(value || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim().length > 0;

/** WordPress may return false instead of an empty array for repeaters. */
export const asArray = (value) => (Array.isArray(value) ? value : []);

/** Resolve relative upload paths against the headless CMS origin. */
export const resolveOpsiUrl = (url) => {
  if (!url || typeof url !== "string") {
    return "";
  }

  if (/^https?:\/\//i.test(url)) {
    return url;
  }

  const path = url.startsWith("/") ? url : `/${url}`;
  return `${API_BASE}${path}`;
};

/** Media objects from ACF include a direct url string. */
export const getMediaUrl = (media) => {
  if (!media) {
    return "";
  }

  if (typeof media === "string") {
    return media;
  }

  return media.url || media.sizes?.medium || media.sizes?.thumbnail || "";
};

export const getBoxSliderItems = (sliderSection) => {
  const items = asArray(sliderSection?.box_slider);
  return items.filter(
    (item) =>
      hasHtmlContent(item?.figure) ||
      hasHtmlContent(item?.header) ||
      hasHtmlContent(item?.body) ||
      hasHtmlContent(item?.button?.button_link) ||
      getMediaUrl(item?.icon),
  );
};

/** Icon + heading cards (no stage figure) — use 3-up layout on desktop. Body is optional. */
export const isBoxSliderIconHeadingItem = (item) => {
  const hasFigure = Boolean(getFigureText(item?.figure));
  const hasIcon = Boolean(getMediaUrl(item?.icon));
  const hasHeader = hasHtmlContent(item?.header);

  return hasIcon && hasHeader && !hasFigure;
};

export const isIconHeadingBoxSlider = (items) =>
  items.length > 0 && items.every(isBoxSliderIconHeadingItem);

/** Plain figure label from ACF (e.g. "1", "2", "3"). */
export const getFigureText = (value) =>
  String(value || "")
    .replace(/<[^>]*>/g, "")
    .trim();

/** ACF colour picker: background_colour (British spelling) on box_slider rows. */
export const resolveBackgroundColour = (item, fallback = "") => {
  const raw = item?.background_colour ?? item?.background_color ?? "";

  if (raw && typeof raw === "object") {
    const fromObject = raw.hex || raw.value || raw.color || "";
    return resolveBackgroundColour({ background_colour: fromObject }, fallback);
  }

  let color = String(raw ?? "").trim();

  if (!color) {
    return fallback;
  }

  // CMS occasionally saves "##fff" — normalise to "#fff"
  if (color.startsWith("##")) {
    color = color.slice(1);
  }

  if (!color.startsWith("#")) {
    color = `#${color}`;
  }

  return color;
};

/** Split quote body HTML into main quote block and trailing citation paragraph. */
export const splitSliderQuoteBody = (html) => {
  if (!hasHtmlContent(html)) {
    return { quoteHtml: "", citationHtml: "" };
  }

  const paragraphs = String(html).match(/<p[^>]*>[\s\S]*?<\/p>/gi) || [];
  const nonEmpty = paragraphs.filter((paragraph) =>
    hasHtmlContent(paragraph),
  );

  if (nonEmpty.length <= 1) {
    return { quoteHtml: html, citationHtml: "" };
  }

  const lastParagraph = nonEmpty[nonEmpty.length - 1];
  const isCitation = !/<strong/i.test(lastParagraph);

  if (!isCitation) {
    return { quoteHtml: html, citationHtml: "" };
  }

  return {
    quoteHtml: nonEmpty.slice(0, -1).join(""),
    citationHtml: lastParagraph,
  };
};
